import { isOrderScriptSent } from "@nexus/contracts";
import type { QueryClient } from "@tanstack/react-query";

import { marketplaceKeys } from "@/lib/queries/marketplace.queries";

import type { MarketplaceOrderStatus, SentScriptsMap } from "../types/marketplace.types";
import {
  ORDER_HANDOFF_POLL_INTERVAL_MS,
  ORDER_HANDOFF_SLOW_THRESHOLD_MS,
} from "./order-handoff-ux";
import { findVendorSend } from "./sent-scripts-map";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export type SentScriptsPollResult =
  | { outcome: "sent"; orderId: string }
  | { outcome: "pending"; orderId: string; status: MarketplaceOrderStatus }
  | { outcome: "timeout" }
  | { outcome: "aborted" };

export function readSentScriptsPollState(
  map: SentScriptsMap | undefined,
  scriptIds: string[],
  vendorId?: string,
): SentScriptsPollResult | null {
  if (!map || scriptIds.length === 0) return null;
  // An item can sit with several partners, so read the order for the partner this send targeted.
  const rows = scriptIds.map((id) => (vendorId ? findVendorSend(map, id, vendorId) : map[id]));
  if (rows.some((row) => row == null)) return null;
  const present = rows.filter((row): row is NonNullable<(typeof rows)[number]> => row != null);
  const orderId = present[0]?.orderId;
  if (!orderId) return null;
  if (present.every((row) => isOrderScriptSent(row))) {
    return { outcome: "sent", orderId };
  }
  return { outcome: "pending", orderId, status: present[0]!.status };
}

/** Polls sent-scripts until every script is SENT, or the handoff budget expires. */
export async function pollSentScriptsConfirmation(
  queryClient: QueryClient,
  tenantId: string,
  scriptIds: string[],
  signal?: AbortSignal,
  vendorId?: string,
): Promise<SentScriptsPollResult> {
  const deadline = Date.now() + ORDER_HANDOFF_SLOW_THRESHOLD_MS;
  let lastPending: Extract<SentScriptsPollResult, { outcome: "pending" }> | null = null;

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      return { outcome: "aborted" };
    }

    await queryClient.refetchQueries({
      queryKey: marketplaceKeys.sentScripts(tenantId),
    });
    const map = queryClient.getQueryData<SentScriptsMap>(
      marketplaceKeys.sentScripts(tenantId),
    );
    const state = readSentScriptsPollState(map, scriptIds, vendorId);
    if (state?.outcome === "sent") {
      await queryClient.refetchQueries({
        queryKey: marketplaceKeys.orders(tenantId),
      });
      return state;
    }
    if (state?.outcome === "pending") {
      lastPending = state;
    }

    await sleep(ORDER_HANDOFF_POLL_INTERVAL_MS);
  }

  return lastPending ?? { outcome: "timeout" };
}
